import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import ReactGA from "react-ga";
import ReactGA4 from "react-ga4";
import { useCallback, useEffect, useRef } from "react";
import consola from "consola";

let initializedReactGa = false;

const useGoogleAnalytics = () => {
  const location = useLocation();
  const config = useSelector((state) => state.config);
  const optanon = useSelector((state) => state.optanon);
  const account = useSelector((state) => state.account);
  const prevGAUserId = useRef(null);

  const initGA = useCallback(() => {
    // const ga_id = "UA-000000-01"; // for testing only
    const { ga_id } = config;

    ReactGA4.initialize(process.env.REACT_APP_MEASUREMENT_ID);
    initializedReactGa = true;

    if (!ga_id) {
      consola.warn("Could not initialize GA as GA ID is missing");
      return;
    }

    consola.info("Initialized GA with following GA ID", ga_id);
    ReactGA.initialize(ga_id);
  }, [config]);

  const isConsentent = useCallback(() => {
    return optanon.activeGroups.includes(1);
  }, [optanon.activeGroups]);

  // Debug purpose only but useful in production too
  useEffect(() => {
    consola.info("Notified of consent update");

    if (!isConsentent()) {
      consola.warn("User did not consent to the use of GA");
      return;
    }

    consola.success("User consented to the use of GA");
  }, [optanon, isConsentent]);

  useEffect(() => {
    if (initializedReactGa || !config || !isConsentent()) {
      return;
    }

    initGA();
  }, [config, optanon, isConsentent, initGA]);

  const setGAUserId = (id) => {
    if (!initializedReactGa) return;
    const platformId = process.env.REACT_APP_DOMAIN?.split(".")?.[0] || null;
    const prefixedUserId = platformId && id ? `${platformId?.trim().toUpperCase()}_${id}` : id;
    ReactGA4.set({ user_id: prefixedUserId });
  };

  const getGAUserId = useCallback(
    (account) => (account && account.id) || null,
    []
  );

  useEffect(() => {
    if (!isConsentent()) return;
    const id = getGAUserId(account);
    if (prevGAUserId.current !== id) {
      setGAUserId(id);
      prevGAUserId.current = id;
    }
  }, [account, isConsentent, getGAUserId]);

  useEffect(() => {
    if (!isConsentent()) {
      return;
    }

    const id = getGAUserId(account);
    if (id) {
      setGAUserId(id);
    }

    ReactGA.pageview(window.location.pathname + window.location.search);
    ReactGA4.send({ hitType: "pageview", page: window.location.pathname + window.location.search, title: window.location.pathname });
  }, [location, isConsentent]);
};

export default useGoogleAnalytics;
